import React from 'react';
import styled, {css} from 'styled-components';

import {colorTheme} from '../../../../mixin/colorTheme';
import {FormLabel} from '../../../form-labels/FormLabel';
import {ToggleBackground, ToggleHandle} from './ToggleFeatures';
import {toggleDirection, toggleFeatureSetup, toggleSize} from './toggleSetup';

/**
 * This component allows you to create multiple checkboxes. It is designed for use cases such as multi-select options.
 *
 * @param {object} param0
 *
 * @param {React.ReactNode} [param0.label]
 * The text to be displayed as the label.
 *
 * @param {'right'} [param0.direction]
 * By default, the toggle component is positioned with the toggle feature on the left and the label on the right.
 * By setting direction='right', you can flip this layout, positioning the toggle on the right and the label on the left.
 *
 * @param {'sm'} [param0.size]
 * The size prop is used to adjust the dimensions of the toggle component. Currently, only a smaller variant is available.
 *
 * @param {string} param0.name
 * The name of the toggle component.
 *
 * @param {boolean} [param0.checked]
 * Specifies whether the toggle switch is in the "on" or "off" position.
 *
 * @param {import('react').ChangeEventHandler<HTMLInputElement>} [param0.onChange]
 * Event handler called when the toggle state changes. It accepts a function to be executed when the toggle is toggled, typically used to update the state of the toggle component.
 *
 * @example
 * ```jsx
 *      <Toggle
 *          direction="right"
 *          size="sm"
 *          checked={isOutgoing}
 *          disabled={!isNewAfvalstroom(window)}
 *          name="is_outgoing"
 *          label={isOutgoing ? 'Uitgaand' : 'Ingaand'}
 *          onChange={toggle}/>
 * ```
 */
const Toggle = ({label, checked, onChange, name, size, theme, direction}) => (
    <ToggleContainer checked={checked} direction={direction} size={size} theme={theme}>
        <input type="checkbox" id={name + 'Toggle'} name={name} checked={checked} onChange={onChange} />
        <ToggleBackground size={size}>
            <ToggleHandle />
        </ToggleBackground>
        <FormLabel size={size} htmlFor={name + 'Toggle'} style={{marginBottom: 0}}>
            {label}
        </FormLabel>
    </ToggleContainer>
);

const ToggleContainer = styled.label`
    --toggle-width: 44px;
    --toggle-height: 20px;
    --toggle-handle: 16px;
    --toggle-handle-position: 3px;

    ${colorTheme({
        '--toggle-handle-bg       ': {dark: 'var(--gray-08)      ', light: 'var(--gray-00)'},
        '--toggle-container-bg    ': {dark: 'var(--black-04)      ', light: 'var(--gray-02)'},
    })};
    display: flex;
    gap: var(--space-xsm);

    ${({checked}) => {
        if (checked) {
            return css`
                ${ToggleHandle} {
                    background: var(--white-00);
                }
            `;
        }
    }}
    ${ToggleBackground} {
        width: var(--toggle-width);
        height: var(--toggle-height);
        ${ToggleHandle} {
            width: var(--toggle-handle);
            height: var(--toggle-handle);
        }
    }
    ${toggleFeatureSetup};
    ${({direction}) => toggleDirection(direction)};
    ${({size}) => toggleSize(size)};
`;

Toggle.Wrapper = ToggleContainer;
export {Toggle, ToggleContainer};
